/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { select, withQuery } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Icon } from '@panel/commons';
import { yupResolver } from '@hookform/resolvers';
import loader from '@panel/commons/assets/icons/loader.gif';
import loaderUBPE from '@panel/commons/assets/icons/loader-urbania.gif';
import loaderVa from '@panel/commons/assets/icons/loader_VA.gif';
import { site } from '@panel/commons/helpers';
import { seekerFormValidations } from './validations';

const selectLoaderLogo = { UBPE: loaderUBPE, VAMX: loaderVa };
const loaderLogo = selectLoaderLogo[site.id] ? selectLoaderLogo[site.id] : loader;

const Container = styled.div`
  max-width: 620px;
  width: 100%;
  height: 535px;
  margin: 0 auto;
  display: flex;
  background-color: ${select('white')};
  flex-flow: column nowrap;
  border-radius: 8px;
  padding: 40px;
  position: relative;
  font-family: 'Hind';
  > div.iconWrapper {
    position: absolute;
    &:hover {
      cursor: pointer;
    }
    top: 22px;
    right: 22px;
    ${withQuery.xs.down()} {
      top: 12px;
      right: 12px;
    }
  }
  align-items: center;
  > form > div:first-child {
    margin: 0 auto;
  }
  ${withQuery.md.down()} {
    height: auto;
    padding: 40px 20px;
    min-height: auto;
    overflow-y: scroll;
    padding-bottom: 100px;
  }
  ${withQuery.sm.down()} {
    height: auto;
    padding: 40px 20px;
    min-height: auto;
    overflow-y: scroll;
    padding-bottom: 100px;
  }
  ${withQuery.xs.down()} {
    height: 100%;
    border-radius: 0px;
    padding: 20px 10px;
    padding-bottom: 100px;
  }
  box-sizing: border-box;
`;

const Title = styled.h4`
  text-align: center;
  font-weight: 600;
  font-size: 20px;
  line-height: 26px;
  margin: 0 auto;
  ${withQuery.xs.down()} {
    padding: 20px 10px;
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  font-size: 16px;
  line-height: 26px;
  color: ${select('darkGray')};
  padding: 5px 100px;
  white-space: break-spaces;
  text-align: center;
  margin-bottom: 20px;
  ${withQuery.md.down()} {
    height: auto;
    margin-bottom: 15px;
    font-size: 16px;
    padding: 5px;
  }
`;

const GridLayout = styled.div`
  display: flex;
  flex-flow: row wrap;
  > div:nth-child(odd) {
    margin-right: 16px;
    ${withQuery.md.down()} {
      margin-right: 0px;
    }
  }
  justify-content: center;
  ${withQuery.md.down()} {
    height: auto;
  }
`;

const FormControl = styled.fieldset`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  align-items: flex-start;
  flex-grow: 1;
  font-family: 'Hind';
  label {
    display: flex;
    font-size: 14px;
    font-weight: 400px;
    flex-direction: column;
    width: 100%;
    max-width: 246px;
  }
  input,
  select {
    box-sizing: border-box;
    padding: 13px 12px;
    margin-top: 5px;
    border: 1px solid ${select('midDarkGray')};
    border-radius: 8px;
    background-color: ${select('white')};
    height: 48px;
    width: 100%;
    font-size: 14px;
    font-weight: 400;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
  select {
    -moz-appearance: none;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")
      no-repeat center right;
    &:hover {
      cursor: pointer;
    }
  }
  width: 100%;
  ${GridLayout} > & {
    max-width: 246px;
    width: 100%;
  }
  min-height: 100px;
  ${withQuery.md.down()} {
    height: auto;
    ${GridLayout} > & {
      max-width: 100%;
      width: 100%;
    }
  }
`;

const ErrorMessageView = styled.span`
  color: ${select('negative')};
  font-size: 12px;
  margin-top: 0px;
  height: 16px;
  max-width: 246px;
  white-space: break-spaces;
  ${withQuery.md.down()} {
    max-width: 340px;
  }
`;

const Submit = styled.button`
  margin: 0 auto;
  width: 176px;
  height: 48px;
  padding: 13px 53px;
  background: ${getColor('primary')};
  border-radius: 12px;
  color: ${select('white')};
  display: block;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  border-radius: 12px;
  &:hover {
    cursor: pointer;
  }
  ${withQuery.md.down()} {
    width: 100%;
  }
`;

const DisabledOverlay = styled.div`
  position: ${props => (props.loading === 'true' ? 'absolute' : 'initial')};
  display: ${props => (props.loading === 'true' ? 'block' : 'none')};
  z-index: 999;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(256, 256, 256, 0.6);
  border-radius: 8px;
  background-image: url(${loaderLogo});
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100px;
`;

const SeekerModal = React.forwardRef(
  ({ taxConditions, loading, onSubmit, onClose, convertionError = null, mobilePhone }, ref) => {
    const [selectedCondition, setSelectedCondition] = useState('');
    const { register, errors, handleSubmit, watch, setError, setValue } = useForm({
      resolver: yupResolver(seekerFormValidations),
      reValidateMode: 'onBlur',
      shouldFocusError: true
    });
    const { t } = useTranslation();
    const $taxCondition = watch('taxCondition', false);

    useEffect(() => {
      if (convertionError && convertionError.fieldError) {
        setError(convertionError.fieldError, { type: 'salesForce', message: convertionError.message });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convertionError]);

    useEffect(() => {
      if (mobilePhone) {
        setValue('mobilePhone', mobilePhone);
      }
    }, [mobilePhone, setValue]);

    useEffect(() => {
      if (taxConditions.length > 0 && $taxCondition !== false) {
        const selected = taxConditions.find(tc => tc.id === $taxCondition) || taxConditions[0];
        setSelectedCondition(selected.description);
      }
    }, [$taxCondition, taxConditions]);

    const handleControlChar = e => {
      if ((e.target.id === 'firstName' || e.target.id === 'lastName') && !e.key.match(/^[a-zA-Z ]$/)) {
        e.preventDefault();
      } else if (e.target.id === 'mobilePhone' && !e.key.match(/^\d+$/)) {
        e.preventDefault();
      }
    };

    return (
      <Container data-repa='seeker-container' ref={ref}>
        <DisabledOverlay data-repa='seeker-loading' loading={loading.toString()} />
        {!loading && (
          <div
            data-repa='modal-seekerConversion-button-close'
            className='iconWrapper'
            role='button'
            tabIndex='0'
            aria-hidden='true'
            onClick={onClose}
          >
            <Icon name='close' colorFill='black' />
          </div>
        )}
        <Title data-repa='seeker-container-title'>{t('publisher.form.convertseeker.title')}</Title>
        <Subtitle data-repa='seeker-container-subtitle'>{t('publisher.form.convertseeker.subtitle')}</Subtitle>
        <form data-repa='modal-seekerConversion-form' onSubmit={handleSubmit(onSubmit)}>
          <GridLayout>
            <FormControl data-repa='seeker-formControl-firstName' className='firstName' disabled={loading}>
              <label data-repa='modal-seekerConversion-form-label-firstName' htmlFor='firstName'>
                {t('publisher.form.convertseeker.name.title')}
              </label>
              <input
                data-repa='modal-seekerConversion-form-input-firstName'
                name='firstName'
                ref={register}
                maxLength='35'
                id='firstName'
                onKeyPress={handleControlChar}
                type='text'
                placeholder={t('publisher.form.convertseeker.name.placeholder')}
              />
              <ErrorMessage
                data-repa='seeker-formControl-error'
                errors={errors}
                name='firstName'
                render={({ message }) => <ErrorMessageView>{t(message)}</ErrorMessageView>}
              />
            </FormControl>
            <FormControl data-repa='seeker-formControl-lastName' className='lastName' disabled={loading}>
              <label data-repa='modal-seekerConversion-form-label-lastName' htmlFor='lastName'>
                {t('publisher.form.convertseeker.lastname.title')}
              </label>
              <input
                data-repa='modal-seekerConversion-form-input-lastName'
                name='lastName'
                ref={register}
                maxLength='35'
                id='lastName'
                onKeyPress={handleControlChar}
                type='text'
                placeholder={t('publisher.form.convertseeker.lastname.placeholder')}
              />
              <ErrorMessage
                data-repa='seeker-formControl-lastName-error'
                errors={errors}
                name='lastName'
                render={({ message }) => <ErrorMessageView>{t(message)}</ErrorMessageView>}
              />
            </FormControl>
            <FormControl data-repa='seeker-formControl-tax' disabled={loading}>
              <label data-repa='modal-seekerConversion-form-label-taxCondition' htmlFor='taxCondition'>
                {t('publisher.form.convertseeker.condition.title')}
              </label>
              <select
                data-repa='modal-seekerConversion-form-select-taxCondition'
                name='taxCondition'
                ref={register}
                id='taxCondition'
                disabled={taxConditions.length === 0}
              >
                {taxConditions.length
                  ? taxConditions.map(({ id, description }) => (
                      <option
                        data-repa={`modal-seekerConversion-form-select-taxCondition-option-${id}`}
                        key={`${id}-${description}`}
                        value={id}
                      >
                        {description}
                      </option>
                    ))
                  : null}
              </select>
            </FormControl>
            <FormControl data-repa='seeker-formControl-taxId' disabled={loading}>
              <label data-repa='modal-seekerConversion-form-label-taxId' htmlFor='taxId'>{`${selectedCondition ||
                t('publisher.form.convertseeker.idnumber.title')}`}</label>
              <input
                data-repa='modal-seekerConversion-form-input-taxId'
                name='taxId'
                ref={register}
                id='taxId'
                maxLength='20'
                type='text'
                placeholder={t('publisher.form.convertseeker.idnumber.placeholder')}
              />
              <ErrorMessage
                data-repa='seeker-formControl-taxId-error'
                errors={errors}
                name='taxId'
                render={({ message }) => <ErrorMessageView>{t(message)}</ErrorMessageView>}
              />
            </FormControl>
            <FormControl data-repa='seeker-formControl-phoneM' disabled={loading}>
              <label data-repa='modal-seekerConversion-form-label-mobilePhone' htmlFor='mobilePhone'>
                {t('publisher.form.convertseeker.mobilePhone.title')}
              </label>
              <input
                data-repa='modal-seekerConversion-form-input-mobilePhone'
                name='mobilePhone'
                maxLength='40'
                ref={register}
                onKeyPress={handleControlChar}
                id='mobilePhone'
                type='number'
                placeholder={t('publisher.form.convertseeker.mobilePhone.placeholder')}
              />
              <ErrorMessage
                data-repa='seeker-formControl-phoneM-error'
                errors={errors}
                name='mobilePhone'
                render={({ message }) => <ErrorMessageView>{t(message)}</ErrorMessageView>}
              />
            </FormControl>
            <FormControl data-repa='seeker-formControl-phone' disabled={loading}>
              <label data-repa='modal-seekerConversion-form-label-phone' htmlFor='phone'>
                {t('publisher.form.convertseeker.phone.title')}
              </label>
              <input
                data-repa='modal-seekerConversion-form-input-phone'
                name='phone'
                maxLength='40'
                ref={register}
                id='phone'
                type='number'
                placeholder={t('publisher.form.convertseeker.phone.placeholder')}
              />
              <ErrorMessage
                data-repa='seeker-formControl-phone-error'
                errors={errors}
                name='phone'
                render={({ message }) => <ErrorMessageView>{t(message)}</ErrorMessageView>}
              />
            </FormControl>
          </GridLayout>
          <Submit data-repa='modal-seekerConversion-form-submit' disabled={loading}>
            {t('publisher.form.convertseeker.cta')}
          </Submit>
        </form>
      </Container>
    );
  }
);

export { SeekerModal };
